const React = require('react')
const { useState, useEffect } = require('react')
const { useHistory } = require('react-router-dom')
const { Row, Col, UncontrolledTooltip, Table } = require('reactstrap')
const {
    useFormatBytes,
    useFormatTimestamp,
    useHexToString,
    useResponsive,
    useDecodeFunctionInput,
    BREAKPOINTS
} = require('@hooks')
const { useNetworkConfig } = require('@contexts')
const { LoadingSpinner, CopyButton, CustomDropdown } = require('@components')

require('@styles/pages/transaction/details.transaction.css')

const TransactionDetails = props => {
    const { loading, data, latestBlock, blockData, isDetailsExpanded, setIsDetailsExpanded, onTabChange } = props
    const history = useHistory()
    const { network } = useNetworkConfig()
    const formatBytes = useFormatBytes()
    const formatTimestamp = useFormatTimestamp()
    const formatHexToString = useHexToString()
    const { getDecodeInput } = useDecodeFunctionInput()
    const isMobile = useResponsive(BREAKPOINTS.small)

    const [transactionDetails, setTransactionDetails] = useState([])
    const [transactionDetailsExpanded, setTransactionDetailsExpanded] = useState([])

    const [inputViewType, setInputViewType] = useState('default')

    useEffect(() => {
        if (!data || !latestBlock || !blockData) return

        const confirmations = latestBlock.number - data.details.blockNumber
        const isPending = confirmations <= 6

        const contractCreation =
            !data.details.to.address || data.details.to.address === '0x0000000000000000000000000000000000000000'

        const fields = {
            divider: {
                id: 'divider'
            },
            hash: {
                id: 'transaction-hash',
                label: 'Transaction Hash',
                value: <>{data.details.hash}</>,
                tooltip:
                    'A TxHash or transaction hash is a unique 66-character identifier that is generated whenever a transaction is executed.'
            },
            status: {
                id: 'transaction-status',
                label: 'Status',
                value: (
                    <>
                        <div
                            id='transaction-status-value'
                            className={`status-box ${
                                data.details.status === true
                                    ? 'success'
                                    : data.details.status === false
                                    ? 'fail'
                                    : 'pending'
                            }`}
                        >
                            <i
                                className={`fas ${
                                    data.details.status === true
                                        ? 'fa-circle-check'
                                        : data.details.status === false
                                        ? 'fa-times-circle'
                                        : 'fa-clock'
                                } status-icon`}
                            />
                            {data.details.status === true
                                ? 'Success'
                                : data.details.status === false
                                ? 'Fail'
                                : 'Pending'}
                        </div>
                        <UncontrolledTooltip placement='top' target='transaction-status-value'>
                            A Status code indicating if the top-level call succeeded or failed (applicable for Post
                            BYZANTIUM blocks only)
                        </UncontrolledTooltip>
                    </>
                ),
                tooltip: 'The finality status of the transaction.'
            },
            block: {
                id: 'transaction-block-number',
                label: 'Block',
                value: (
                    <>
                        {data.details.blockNumber ? (
                            <>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <div id='transaction-block-icon' className='tx-status-indicator'>
                                        <i
                                            className={`fas ${isPending ? 'fa-hourglass-start' : 'fa-check'}`}
                                            style={{
                                                fontSize: '10px',
                                                backgroundColor: `${
                                                    isPending ? 'var(--pending-color)' : 'var(--success-color)'
                                                }`,
                                                padding: '8px'
                                            }}
                                        />
                                    </div>
                                    <span
                                        className={`${network.name}-text-color cursor-pointer`}
                                        onClick={() => {
                                            history.push(`/${network.name}/block/${data.details.blockNumber}`)
                                        }}
                                    >
                                        &nbsp;
                                        {data.details.blockNumber}
                                    </span>
                                    <div
                                        id='transaction-block-confirmations'
                                        className={`status-box ${isPending ? 'pending' : 'success'}`}
                                        style={{ marginLeft: '12px' }}
                                    >
                                        {confirmations} Block Confirmation
                                        {confirmations > 1 && 's'}
                                    </div>
                                </div>
                                <UncontrolledTooltip placement='top' target='transaction-block-icon'>
                                    {isPending
                                        ? 'This block is unfinalized and may be susceptible to reorgs.'
                                        : "The block is finalized and cannot be reverted without slashing at least 1/3 of all validators's stake."}
                                </UncontrolledTooltip>
                                <UncontrolledTooltip placement='top' target='transaction-block-confirmations'>
                                    Number of block produced since
                                </UncontrolledTooltip>
                            </>
                        ) : (
                            <>(Pending)</>
                        )}
                    </>
                ),
                tooltip:
                    'Number of block in which transaction is recorded. Blocks confirmations indicate how many blocks have been added since the transaction was produced.'
            },
            timestamp: {
                id: 'transaction-timestamp',
                label: data.details.blockTimestamp ? 'Timestamp' : 'Time Last Seen',
                value: (
                    <>
                        {data.details.blockTimestamp ? (
                            <>
                                <i className='fas fa-clock' style={{ fontSize: '12px' }} />
                                &nbsp;
                                {formatTimestamp(data.details.blockTimestamp)}
                            </>
                        ) : (
                            <>{formatTimestamp(latestBlock.timestamp)}</>
                        )}
                    </>
                ),
                tooltip: data.details.blockTimestamp
                    ? 'The date and time at which a transaction is produced.'
                    : 'The time when the transaction is last seen in the transaction pool'
            },

            action: {
                id: 'transaction-action',
                label: 'Transaction Action',
                value: <></>,
                tooltip: 'Highlighted events of the transaction.'
            },
            from: {
                id: 'transaction-from',
                label: 'From',
                value: (
                    <>
                        {data.details.from.type === 'SCA' && (
                            <>
                                <i
                                    className='fas fa-file-alt'
                                    style={{
                                        fontSize: '14px',
                                        color: 'var(--grey-color)'
                                    }}
                                />
                                &nbsp;
                            </>
                        )}
                        <span
                            id='transaction-from-address'
                            className={`${network.name}-text-color cursor-pointer`}
                            onClick={() => {
                                history.push(`/${network.name}/address/${data.details.from.address}`)
                            }}
                        >
                            {formatBytes(data.details.from.address, 50, 42, 0)}
                        </span>
                        <UncontrolledTooltip placement='top' target='transaction-from-address'>
                            {data.details.from.address}
                        </UncontrolledTooltip>
                        &nbsp;
                        <CopyButton
                            id={`copy-transaction-from-${data.details.hash}`}
                            data={data.details.from.address}
                            text={'Copy address'}
                        />
                    </>
                ),
                tooltip: 'The sending party of the transaction.'
            },
            to: {
                id: 'transaction-to',
                label: data.details.to.type === 'SCA' ? 'Interacted With (To)' : 'To',
                value: (
                    <>
                        {contractCreation ? (
                            <>
                                [&nbsp;
                                <i
                                    className='fas fa-file-alt'
                                    style={{
                                        fontSize: '14px',
                                        color: 'var(--grey-color)'
                                    }}
                                />
                                &nbsp;
                                <span
                                    id='transaction-to-address'
                                    className={`${network.name}-text-color cursor-pointer`}
                                    onClick={() => {
                                        history.push(`/${network.name}/address/${data.details.contractAddress}`)
                                    }}
                                >
                                    {formatBytes(data.details.contractAddress, 50, 42, 0)}
                                </span>
                                <UncontrolledTooltip placement='top' target='transaction-to-address'>
                                    {data.details.contractAddress}
                                </UncontrolledTooltip>
                                &nbsp;Created]&nbsp;
                                <CopyButton
                                    id={`copy-transaction-to-${data.details.hash}`}
                                    data={data.details.contractAddress}
                                    text={'Copy address'}
                                />
                            </>
                        ) : (
                            <>
                                {data.details.to.type === 'SCA' && (
                                    <>
                                        <i
                                            className='fas fa-file-alt'
                                            style={{
                                                fontSize: '14px',
                                                color: 'var(--grey-color)'
                                            }}
                                        />
                                        &nbsp;
                                    </>
                                )}
                                <span
                                    id='transaction-to-address'
                                    className={`${network.name}-text-color cursor-pointer`}
                                    onClick={() => {
                                        history.push(`/${network.name}/address/${data.details.to.address}`)
                                    }}
                                >
                                    {formatBytes(data.details.to.address, 50, 42, 0)}
                                </span>
                                <UncontrolledTooltip placement='top' target='transaction-to-address'>
                                    {data.details.to.address}
                                </UncontrolledTooltip>
                                &nbsp;
                                <CopyButton
                                    id={`copy-transaction-to-${data.details.hash}`}
                                    data={data.details.to.address}
                                    text={'Copy address'}
                                />
                            </>
                        )}
                    </>
                ),
                tooltip: 'The receiving party of the transaction (could be a contract address).'
            },
            erc20Transferred: {
                id: 'transaction-erc20-transferred',
                label: 'ERC-20 Tokens Transferred',
                value: (
                    <>
                        {data.tokenTransfers
                            .filter(transaction => transaction.tokenType === 'ERC20')
                            .map((transaction, index) => (
                                <div key={index}>
                                    <span style={{ fontWeight: 'bold' }}>From:</span>&nbsp;
                                    <span id={`transaction-erc20-transferred-details-${index}`}>
                                        {transaction.from.type === 'SCA' && (
                                            <>
                                                <i
                                                    className='fas fa-file-alt'
                                                    style={{
                                                        fontSize: '14px',
                                                        color: 'var(--grey-color)'
                                                    }}
                                                />
                                                &nbsp;
                                            </>
                                        )}
                                        <span
                                            id={`transaction-erc20-transferred-details-${index}-from-address`}
                                            className={`${network.name}-text-color cursor-pointer`}
                                            onClick={() => {
                                                history.push(`/${network.name}/address/${transaction.from.address}`)
                                            }}
                                        >
                                            {formatBytes(transaction.from.address, 20, 9, 8)}
                                        </span>
                                        <UncontrolledTooltip
                                            placement='top'
                                            target={`transaction-erc20-transferred-details-${index}-from-address`}
                                        >
                                            {transaction.from.address}
                                        </UncontrolledTooltip>
                                        &nbsp;
                                        <CopyButton
                                            id={`copy-transaction-from-${data.details.hash}`}
                                            data={transaction.from.address}
                                            text={'Copy address'}
                                        />
                                    </span>
                                    &nbsp;<span style={{ fontWeight: 'bold' }}>To:</span>&nbsp;
                                    <span id={`transaction-erc20-transferred-details-${index}`}>
                                        {transaction.to.type === 'SCA' && (
                                            <>
                                                <i
                                                    className='fas fa-file-alt'
                                                    style={{
                                                        fontSize: '14px',
                                                        color: 'var(--grey-color)'
                                                    }}
                                                />
                                                &nbsp;
                                            </>
                                        )}
                                        <span
                                            id={`transaction-erc20-transferred-details-${index}-to-address`}
                                            className={`${network.name}-text-color cursor-pointer`}
                                            onClick={() => {
                                                history.push(`/${network.name}/address/${transaction.to.address}`)
                                            }}
                                        >
                                            {formatBytes(transaction.to.address, 20, 9, 8)}
                                        </span>
                                        <UncontrolledTooltip
                                            placement='top'
                                            target={`transaction-erc20-transferred-details-${index}-to-address`}
                                        >
                                            {transaction.to.address}
                                        </UncontrolledTooltip>
                                        &nbsp;
                                        <CopyButton
                                            id={`copy-transaction-to-${data.details.hash}`}
                                            data={transaction.to.address}
                                            text={'Copy address'}
                                        />
                                    </span>
                                    &nbsp;For&nbsp;
                                    <span>
                                        {(transaction.tokenValue / 10 ** transaction.tokenDecimals).toLocaleString(
                                            undefined,
                                            {
                                                minimumFractionDigits: 0,
                                                maximumFractionDigits: transaction.tokenDecimals
                                            }
                                        )}
                                    </span>
                                    &nbsp;
                                    <span
                                        id={`transaction-erc20-transferred-details-${index}-token-address`}
                                        className={`${network.name}-text-color cursor-pointer`}
                                        onClick={() => {
                                            history.push(`/${network.name}/address/${transaction.tokenAddress}`)
                                        }}
                                    >
                                        {transaction.tokenName}
                                    </span>
                                    <UncontrolledTooltip
                                        placement='top'
                                        target={`transaction-erc20-transferred-details-${index}-token-address`}
                                    >
                                        {transaction.tokenAddress}
                                    </UncontrolledTooltip>
                                    &nbsp;
                                    <span style={{ color: 'var(--grey-color)' }}>({transaction.tokenSymbol})</span>
                                </div>
                            ))}
                    </>
                ),
                tooltip: `List of ERC-20 tokens transferred in the transaction.`
            },
            erc721Transferred: {
                id: 'transaction-erc721-transferred',
                label: 'ERC-721 Tokens Transferred',
                value: (
                    <>
                        {data.tokenTransfers
                            .filter(transaction => transaction.tokenType === 'ERC721')
                            .map((transaction, index) => (
                                <div key={index}>
                                    <div>
                                        <span style={{ color: 'var(--grey-color)' }}>{transaction.tokenType}</span>
                                        &nbsp;Token ID [
                                        <span
                                            className={`${network.name}-text-color cursor-pointer`}
                                            onClick={() => {
                                                history.push(
                                                    `/${network.name}/token/${transaction.tokenAddress}?a=${transaction.tokenId}`
                                                )
                                            }}
                                        >
                                            {transaction.tokenId}
                                        </span>
                                        ]&nbsp;
                                        <span
                                            id={`transaction-erc721-transferred-details-${index}-token-address`}
                                            className={`${network.name}-text-color cursor-pointer`}
                                            onClick={() => {
                                                history.push(`/${network.name}/address/${transaction.tokenAddress}`)
                                            }}
                                        >
                                            {transaction.tokenName}
                                        </span>
                                        <UncontrolledTooltip
                                            placement='top'
                                            target={`transaction-erc721-transferred-details-${index}-token-address`}
                                        >
                                            {transaction.tokenAddress}
                                        </UncontrolledTooltip>
                                        &nbsp;
                                        <span style={{ color: 'var(--grey-color)' }}>({transaction.tokenSymbol})</span>
                                    </div>
                                    <div>
                                        <span style={{ fontWeight: 'bold' }}>From:</span>&nbsp;
                                        <span id={`transaction-erc721-transferred-details-${index}`}>
                                            {transaction.from.type === 'SCA' && (
                                                <>
                                                    <i
                                                        className='fas fa-file-alt'
                                                        style={{
                                                            fontSize: '14px',
                                                            color: 'var(--grey-color)'
                                                        }}
                                                    />
                                                    &nbsp;
                                                </>
                                            )}
                                            <span
                                                id={`transaction-erc721-transferred-details-${index}-from-address`}
                                                className={`${network.name}-text-color cursor-pointer`}
                                                onClick={() => {
                                                    history.push(`/${network.name}/address/${transaction.from.address}`)
                                                }}
                                            >
                                                {formatBytes(transaction.from.address, 20, 9, 8)}
                                            </span>
                                            <UncontrolledTooltip
                                                placement='top'
                                                target={`transaction-erc721-transferred-details-${index}-from-address`}
                                            >
                                                {transaction.from.address}
                                            </UncontrolledTooltip>
                                            &nbsp;
                                            <CopyButton
                                                id={`copy-transaction-from-${data.details.hash}`}
                                                data={transaction.from.address}
                                                text={'Copy address'}
                                            />
                                        </span>
                                        &nbsp;<span style={{ fontWeight: 'bold' }}>To:</span>&nbsp;
                                        <span id={`transaction-erc721-transferred-details-${index}`}>
                                            {transaction.to.type === 'SCA' && (
                                                <>
                                                    <i
                                                        className='fas fa-file-alt'
                                                        style={{
                                                            fontSize: '14px',
                                                            color: 'var(--grey-color)'
                                                        }}
                                                    />
                                                    &nbsp;
                                                </>
                                            )}
                                            <span
                                                id={`transaction-erc721-transferred-details-${index}-to-address`}
                                                className={`${network.name}-text-color cursor-pointer`}
                                                onClick={() => {
                                                    history.push(`/${network.name}/address/${transaction.to.address}`)
                                                }}
                                            >
                                                {formatBytes(transaction.to.address, 20, 9, 8)}
                                            </span>
                                            <UncontrolledTooltip
                                                placement='top'
                                                target={`transaction-erc721-transferred-details-${index}-to-address`}
                                            >
                                                {transaction.to.address}
                                            </UncontrolledTooltip>
                                            &nbsp;
                                            <CopyButton
                                                id={`copy-transaction-to-${data.details.hash}`}
                                                data={transaction.to.address}
                                                text={'Copy address'}
                                            />
                                        </span>
                                    </div>
                                </div>
                            ))}
                    </>
                ),
                tooltip: `List of ERC-721 tokens transferred in the transaction.`
            },
            value: {
                id: 'transaction-value',
                label: 'Value',
                value: (
                    <>
                        <span id='transaction-value-details'>
                            {network.data.networkIcon && (
                                <img src={network.data.networkIcon} width='16px' style={{ marginRight: '8px' }} />
                            )}
                            {(data.details.value / 10 ** 18).toLocaleString(undefined, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 18
                            })}
                            &nbsp;
                            {network.data.token}
                        </span>
                        <UncontrolledTooltip placement='top' target='transaction-value-details'>
                            The amount of {network.data.token} to be transferred to the receipient with the transaction.
                        </UncontrolledTooltip>
                    </>
                ),
                tooltip: `The value being transacted in ${network.data.token} and fiat value.`
            },
            fee: {
                id: 'transaction-fee',
                label: data.details.gasUsed ? 'Transaction Fee' : 'Max Txn Cost/Fee',
                value: (
                    <>
                        <span id='transaction-fee-details'>
                            {(
                                ((data.details.gasUsed || data.details.gas) * data.details.gasPrice) /
                                10 ** 18
                            ).toLocaleString(undefined, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 18
                            })}
                            &nbsp;
                            {network.data.token}
                        </span>
                        <UncontrolledTooltip placement='top' target='transaction-fee-details'>
                            Gas Price * Gas Used by transaction.
                        </UncontrolledTooltip>
                    </>
                ),
                tooltip: data.details.gasUsed
                    ? `Amount paid to process the transaction in ${network.data.token} and fiat value.`
                    : 'Due to fluctuation of price, the cost will vary till mined.'
            },
            gasPrice: {
                id: 'transaction-gas-price',
                label: 'Gas Price',
                value: (
                    <>
                        <span id='transaction-gas-price-details'>
                            {(data.details.gasPrice / 10 ** 9).toLocaleString(undefined, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 9
                            })}
                            &nbsp;Gwei&nbsp;
                            <span style={{ color: 'var(--grey-color)' }}>
                                (
                                {(data.details.gasPrice / 10 ** 18).toLocaleString(undefined, {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 18
                                })}
                                &nbsp;
                                {network.data.token})
                            </span>
                        </span>
                        <UncontrolledTooltip placement='top' target='transaction-gas-price-details'>
                            The price offered to the block producer to purchase this amount of GAS (per GAS).
                        </UncontrolledTooltip>
                    </>
                ),
                tooltip: `Cost per unit of gas spent for the transaction. In ${network.data.token} and Gwei.`
            },
            gasLimitAndGasUsed: {
                id: 'transaction-gas-limit-and-gas-used',
                label: 'Gas Limit & Usage by Txn',
                value: (
                    <>
                        <span id='transaction-gas-limit-details'>{data.details.gas.toLocaleString()}</span>
                        <UncontrolledTooltip placement='top' target='transaction-gas-limit-details'>
                            The amount of GAS supplied for this transaction to happen.
                        </UncontrolledTooltip>
                        &nbsp;|&nbsp;
                        {data.details.gasUsed ? (
                            <>
                                <span id='transaction-gas-used-details'>
                                    {data.details.gasUsed.toLocaleString()}
                                    &nbsp;(
                                    {((data.details.gasUsed / data.details.gas) * 100).toLocaleString(undefined, {
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 2
                                    })}
                                    %)
                                </span>
                                <UncontrolledTooltip placement='top' target='transaction-gas-used-details'>
                                    The amount of GAS used by this specific transaction alone.
                                </UncontrolledTooltip>
                            </>
                        ) : (
                            <>(Pending)</>
                        )}
                    </>
                ),
                tooltip: `Maximum amount of the gas allocated for the transaction & the amount eventually used. Normal ${network.data.token} transfers invlove 21,000 gas units while contracts involve higher values.`
            },
            gasFees: {
                id: 'transaction-gas-fees',
                label: 'Gas Fees',
                value: (
                    <>
                        <span id='transaction-gas-fees-base'>
                            <span style={{ color: 'var(--grey-color)' }}>Base: </span>
                            {(blockData.baseFeePerGas / 10 ** 9).toLocaleString(undefined, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 9
                            })}
                            &nbsp;Gwei
                        </span>
                        <UncontrolledTooltip placement='top' target='transaction-gas-fees-base'>
                            {(blockData.baseFeePerGas / 10 ** 18).toLocaleString(undefined, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 18
                            })}
                            &nbsp;
                            {network.data.token}
                        </UncontrolledTooltip>
                        {data.details.transactionTypes !== 0 && (
                            <>
                                <span id='transaction-gas-fees-max'>
                                    <span style={{ color: 'var(--grey-color)' }}> | Max: </span>
                                    {(data.details.maxFeePerGas / 10 ** 9).toLocaleString(undefined, {
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 9
                                    })}
                                    &nbsp;Gwei
                                </span>
                                <UncontrolledTooltip placement='top' target='transaction-gas-fees-max'>
                                    {(data.details.maxFeePerGas / 10 ** 18).toLocaleString(undefined, {
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 18
                                    })}
                                    &nbsp;
                                    {network.data.token}
                                </UncontrolledTooltip>
                                <span id='transaction-gas-fees-max-priority'>
                                    <span style={{ color: 'var(--grey-color)' }}> | Max Priority: </span>
                                    {(data.details.maxPriorityFeePerGas / 10 ** 9).toLocaleString(undefined, {
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 9
                                    })}
                                    &nbsp;Gwei
                                </span>
                                <UncontrolledTooltip placement='top' target='transaction-gas-fees-max-priority'>
                                    {(data.details.maxPriorityFeePerGas / 10 ** 18).toLocaleString(undefined, {
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 18
                                    })}
                                    &nbsp;
                                    {network.data.token}
                                </UncontrolledTooltip>
                            </>
                        )}
                    </>
                ),
                tooltip:
                    'Base Fee rewards to the network Base Fee at the time of the block, while Max Fee and & Max Priority Fee refer to the max amount a user is willing to pay for their tx and to give their block producer respectively.'
            },
            burntAndTxnSavingsFees: {
                id: 'transaction-burnt-and-txn-savings-fees',
                label: 'Burnt & Txn Savings Fees',
                value: <></>,
                tooltip: `Total amount of ${network.data.token} burnt from this tx & total fees saved from the amount the user willing to pay for this tx.`
            },
            totalBlobs: {
                id: 'transaction-blobs',
                label: 'Total Blobs',
                value: (
                    <>
                        {data.details.blobVersionedHashes.length ? (
                            <span
                                className={`${network.name}-text-color cursor-pointer`}
                                onClick={() => onTabChange('blobs')}
                            >
                                {data.details.blobVersionedHashes.length} blob
                                {data.details.blobVersionedHashes.length !== 1 && 's'}
                            </span>
                        ) : (
                            <>{data.details.blobVersionedHashes.length} blob</>
                        )}
                    </>
                ),
                tooltip: 'Total number of EIP 4844 blobs found in this transaction.'
            },
            other: {
                id: 'transaction-other',
                label: 'Other Attributes',
                value: (
                    <>
                        <div className={`status-box pending`} style={{ marginRight: '10px' }}>
                            Txn Type:&nbsp;
                            <span style={{ color: 'var(--white-color)' }}>
                                {data.details.type}&nbsp;
                                {data.details.type === 0 ? (
                                    <>(Legacy)</>
                                ) : data.details.type === 1 ? (
                                    <>(EIP-2930)</>
                                ) : data.details.type === 2 ? (
                                    <>(EIP-1559)</>
                                ) : data.details.type === 3 ? (
                                    <>(EIP-4844)</>
                                ) : null}
                            </span>
                        </div>
                        <div className={`status-box pending`} style={{ marginRight: '10px' }}>
                            Nonce:&nbsp;
                            <span style={{ color: 'var(--white-color)' }}>{data.details.nonce}</span>
                        </div>
                        <div className={`status-box pending`}>
                            Position In Block:&nbsp;
                            <span style={{ color: 'var(--white-color)' }}>{data.details.transactionIndex}</span>
                        </div>
                    </>
                ),
                tooltip: 'Other data related to this transaction.'
            },
            input: {
                id: 'transaction-input',
                label: 'Input Data',
                value: (
                    <>
                        <div
                            style={{
                                width: '100%',
                                height: '100px',
                                backgroundColor: 'var(--pending-background-color)',
                                color: 'var(--grey-color)',
                                padding: '8px 12px',
                                borderRadius: '4px',
                                wordBreak: 'break-all',
                                overflowY: 'auto'
                            }}
                        >
                            {inputViewType === 'default' ? (
                                <>
                                    {contractCreation ? (
                                        <>{data.details.input}</>
                                    ) : (
                                        <>
                                            Function: {data.details.functionName}
                                            <br />
                                            <br />
                                            MethodID: {data.details.input.slice(0, 10)}
                                            <br />
                                            {data.details.input &&
                                                data.details.input
                                                    .slice(10)
                                                    .match(/.{1,64}/g)
                                                    ?.map((chunk, index) => (
                                                        <div key={index}>
                                                            [{index}]: {chunk}
                                                        </div>
                                                    ))}
                                        </>
                                    )}
                                </>
                            ) : inputViewType === 'decoded' ? (
                                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                                    <Table responsive className='tx-details-input'>
                                        <thead>
                                            <tr>
                                                <th style={{ width: '50px' }}>#</th>
                                                <th style={{ width: '150px' }}>Name</th>
                                                <th style={{ width: '100px' }}>Type</th>
                                                <th>Data</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {getDecodeInput(data.details.abi, data.details.input).map(item => (
                                                <tr key={item.index}>
                                                    <td>{item.index}</td>
                                                    <td>{item.name}</td>
                                                    <td>{item.type}</td>
                                                    <td>
                                                        {item.type === 'address' ? (
                                                            <span
                                                                className={`${network.name}-text-color cursor-pointer`}
                                                                onClick={() =>
                                                                    history.push(
                                                                        `/${network.name}/address/${item.value}`
                                                                    )
                                                                }
                                                            >
                                                                {item.value}
                                                            </span>
                                                        ) : (
                                                            <>{item.value}</>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            ) : inputViewType === 'utf8' ? (
                                <>{formatHexToString(data.details.input)}</>
                            ) : (
                                <>{data.details.input}</>
                            )}
                        </div>

                        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center' }}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor: 'var(--pending-background-color)',
                                    padding: '8px 12px',
                                    borderRadius: '4px'
                                }}
                            >
                                <CustomDropdown
                                    className='input-view-dropdown'
                                    currentItem={{
                                        title: 'View Input As'
                                    }}
                                    listItems={[
                                        {
                                            name: (
                                                <>
                                                    {inputViewType === 'default' && <i className='fas fa-check me-2' />}
                                                    Default View
                                                </>
                                            ),
                                            value: 'default'
                                        },
                                        ...(data.details.abi.name
                                            ? [
                                                  {
                                                      name: (
                                                          <>
                                                              {inputViewType === 'decoded' && (
                                                                  <i className='fas fa-check me-2' />
                                                              )}
                                                              Decode Input Data
                                                          </>
                                                      ),
                                                      value: 'decoded'
                                                  }
                                              ]
                                            : []),
                                        {
                                            name: (
                                                <>
                                                    {inputViewType === 'utf8' && <i className='fas fa-check me-2' />}
                                                    UTF-8
                                                </>
                                            ),
                                            value: 'utf8'
                                        },
                                        {
                                            name: (
                                                <>
                                                    {inputViewType === 'original' && (
                                                        <i className='fas fa-check me-2' />
                                                    )}
                                                    Original
                                                </>
                                            ),
                                            value: 'original'
                                        }
                                    ]}
                                    handleClick={value => {
                                        setInputViewType(value)
                                    }}
                                />
                            </div>
                        </div>
                    </>
                ),
                tooltip:
                    'Additional data included for this transaction. Commonly used as part of contract interaction or as a message sent to the receipient.'
            }
        }

        const transactionOrder = [
            'hash',
            'status',
            'block',
            'timestamp',
            'divider',
            'from',
            'to',
            'divider',
            ...(data.tokenTransfers.filter(transaction => transaction.tokenType === 'ERC20').length > 0
                ? ['erc20Transferred', 'divider']
                : []),
            ...(data.tokenTransfers.filter(transaction => transaction.tokenType === 'ERC721').length > 0
                ? ['erc721Transferred', 'divider']
                : []),
            'value',
            'fee',
            'gasPrice'
        ]

        setTransactionDetails(transactionOrder.map(key => fields[key]))

        const transactionTypes = {
            0: ['gasLimitAndGasUsed', 'gasFees', 'divider', 'other', 'input'],
            1: ['gasLimitAndGasUsed', 'gasFees', 'divider', 'other', 'input'],
            2: ['gasLimitAndGasUsed', 'gasFees', 'divider', 'other', 'input'],
            3: ['gasLimitAndGasUsed', 'gasFees', 'divider', 'totalBlobs', 'other', 'input']
        }

        const transactionExpandedOrder = transactionTypes[data.details.type]

        setTransactionDetailsExpanded(transactionExpandedOrder.map(key => fields[key]))
    }, [data, latestBlock, blockData, inputViewType])

    return (
        <>
            {loading ? (
                <div className='box table-section'>
                    <div className='d-flex justify-content-center align-items-center' style={{ height: '300px' }}>
                        <LoadingSpinner />
                    </div>
                </div>
            ) : (
                <>
                    <div className='box table-section'>
                        {transactionDetails.map(item => (
                            <Row className='table-info-row' noGutters>
                                {item.id === 'divider' ? (
                                    <div className='table-divider' />
                                ) : (
                                    <>
                                        <Col xs={12} sm={4} className='d-flex align-items-start'>
                                            <span className='info-icon-wrapper' id={item.id}>
                                                <i className='fas fa-question-circle info-icon' />
                                            </span>
                                            <span className='info-label'>{item.label}:</span>
                                        </Col>
                                        <UncontrolledTooltip placement='top' target={item.id}>
                                            {item.tooltip}
                                        </UncontrolledTooltip>
                                        <Col
                                            xs={12}
                                            sm={8}
                                            className={`d-flex align-items-center ${isMobile && 'mt-2'}`}
                                            style={{ flexWrap: 'wrap', wordBreak: 'break-all' }}
                                        >
                                            <span className='info-value' style={{ width: '100%' }}>
                                                {item.value}
                                            </span>
                                        </Col>
                                    </>
                                )}
                            </Row>
                        ))}
                    </div>

                    <div className='box table-section'>
                        {isDetailsExpanded && (
                            <>
                                {transactionDetailsExpanded.map(item => (
                                    <Row className='table-info-row' noGutters>
                                        {item.id === 'divider' ? (
                                            <div className='table-divider' />
                                        ) : (
                                            <>
                                                <Col xs={12} sm={4} className='d-flex align-items-start'>
                                                    <span className='info-icon-wrapper' id={item.id}>
                                                        <i className='fas fa-question-circle info-icon' />
                                                    </span>
                                                    <span className='info-label'>{item.label}:</span>
                                                </Col>
                                                <UncontrolledTooltip placement='top' target={item.id}>
                                                    {item.tooltip}
                                                </UncontrolledTooltip>
                                                <Col
                                                    xs={12}
                                                    sm={8}
                                                    className={`d-flex align-items-center ${isMobile && 'mt-2'}`}
                                                    style={{ flexWrap: 'wrap', wordBreak: 'break-all' }}
                                                >
                                                    <span className='info-value' style={{ width: '100%' }}>
                                                        {item.value}
                                                    </span>
                                                </Col>
                                            </>
                                        )}
                                    </Row>
                                ))}
                            </>
                        )}
                        <Row
                            className='table-info-row'
                            noGutters
                            style={{ cursor: 'pointer' }}
                            onClick={() => setIsDetailsExpanded(!isDetailsExpanded)}
                        >
                            <Col xs={12} sm={4} className='d-flex align-items-center'>
                                <span className='info-label'>More Details:</span>
                            </Col>
                            <Col xs={12} sm={8} className={`d-flex align-items-center ${isMobile && 'mt-2'}`}>
                                <span className={`info-value ${network.name}-text-color`}>
                                    {isDetailsExpanded ? '- Click to show less' : '+ Click to show more'}
                                </span>
                            </Col>
                        </Row>
                    </div>
                </>
            )}
        </>
    )
}

module.exports = { TransactionDetails }
